import "../styles/gwpbanner.scss";
import * as CubeTrackingAK from "./tracking/cube/AktionskommunikationsTracking";
import { gwpbanner, middle } from "./CAMPWindow";
import * as CubeTrackingAB from "./tracking/cube/AbTestTracking";
import "./CampModal";
import { CampModal } from "./CampModal";

const url = "/entd/campaigns/render/gwpbanner";

export type VariantenWechselDetails = {
    syan: number;
    farbId: string | null;
    artikelId: string | null;
};

class Gwpbanner extends HTMLElement {
    private infoTextTrigger: HTMLElement | undefined | null;
    private infoTextModal: CampModal | undefined | null;
    private kampagneName: string | undefined | null;
    private observer: IntersectionObserver;
    private alreadyTracked: boolean;
    private internalEventsAbortController: AbortController | undefined;
    private static observedAttributes: string[] = ["syan", "artikel-id", "farb-id"];
    private pathPrefix: string | null | undefined;
    private prevAttr: VariantenWechselDetails | null;

    constructor() {
        super();
        this.observer = new IntersectionObserver(this.handleObservationEvent.bind(this), { threshold: 0.9 });
        this.alreadyTracked = false;
        this.prevAttr = this.getObservedAttributes();
    }

    connectedCallback(): void {
        this.setupDOMEventHandling();
    }

    private handleObservationEvent(entries: IntersectionObserverEntry[]): void {
        entries.forEach((entry) => {
            if (entry.isIntersecting && !this.alreadyTracked && this.kampagneName) {
                CubeTrackingAK.trackView(this.kampagneName, middle, gwpbanner);
                const el = this.querySelector<HTMLDivElement>('[data-select="gwpbanner"]');
                const abTestHeaderName = el?.getAttribute("data-abtest-name");
                const abTestHeaderValue = el?.getAttribute("data-abtest-value");
                CubeTrackingAB.trackView(abTestHeaderValue, abTestHeaderName);
                this.alreadyTracked = true;
            }
        });
    }

    disconnectedCallback(): void {
        this.observer.disconnect();
        this.internalEventsAbortController?.abort();
    }

    private setupInfoLayer(signal: AbortSignal): void {
        this.infoTextModal = this.querySelector<CampModal>("#gwpbanner-infotext");
        this.infoTextTrigger = this.querySelector<HTMLElement>("#gwpbanner-infotext-trigger");
        this.infoTextTrigger?.addEventListener("click", this.handleInfoLayerOpenClick.bind(this), { signal });
    }

    private handleInfoLayerOpenClick(): void {
        if (this.kampagneName) {
            CubeTrackingAK.trackDetailClick(this.kampagneName, middle, gwpbanner);
        }
        this.infoTextModal?.classList.remove("display-none");
        this.infoTextModal?.show();
    }

    private setupDOMEventHandling(): void {
        this.internalEventsAbortController = new AbortController();
        this.kampagneName = this.querySelector<HTMLDivElement>('[data-select="gwpbanner"]')?.getAttribute("data-kampagne-name");
        this.pathPrefix = this.getAttribute("data-path-prefix");
        const signal = this.internalEventsAbortController.signal;
        this.setupInfoLayer(signal);
        this.observer.observe(this);
    }

    private getObservedAttributes(): VariantenWechselDetails | null {
        const syanString = this.getAttribute("syan");
        const artikelId = this.getAttribute("artikel-id");
        const farbId = this.getAttribute("farb-id");

        if (!syanString) {
            return null;
        }
        if (Number.isNaN(parseInt(syanString, 10))) {
            return null;
        }
        const syan = parseInt(syanString, 10);

        return { syan, farbId, artikelId };
    }

    attributeChangedCallback(_name: string, _oldValue: string, _newValue: string): void {
        let timer: string | number | NodeJS.Timeout;
        const debounced: () => void = () => {
            clearTimeout(timer);
            timer = setTimeout(() => {
                const attr = this.getObservedAttributes();
                if (attr && this.attibutesChanged(attr)) {
                    void this.fetchContent(attr);
                    this.prevAttr = attr;
                }
            }, 50);
        };
        debounced();
    }

    private async fetchContent(attributes: VariantenWechselDetails): Promise<void> {
        if (!this.pathPrefix) {
            return Promise.reject(new Error("data-path-prefix is required"));
        }
        const path = this.pathPrefix + url;
        const searchParams = new URLSearchParams();
        if (attributes.syan) {
            searchParams.append("syan", String(attributes.syan));
        } else {
            return Promise.reject(new Error("Syan is required"));
        }
        if (attributes.farbId) {
            searchParams.append("farbId", attributes.farbId);
        }
        if (attributes.artikelId) {
            searchParams.append("artikelId", attributes.artikelId);
        }
        const params = searchParams.toString();
        const request = path + "?" + params;

        return fetch(request, {
            headers: {
                "cube-pb-force": "1",
            },
        })
            .then((response) => {
                if (response.status === 200) {
                    return response.text();
                } else {
                    return null;
                }
            })
            .then((content) => {
                if (content !== null) {
                    this.internalEventsAbortController?.abort();
                    this.observer.unobserve(this);
                    this.alreadyTracked = false;
                    this.innerHTML = content;
                    this.setupDOMEventHandling();
                }
                return void 0;
            });
    }

    private attibutesChanged(a: VariantenWechselDetails): boolean {
        return this.prevAttr?.syan !== a.syan || this.prevAttr?.farbId !== a.farbId || this.prevAttr?.artikelId !== a.artikelId;
    }
}

customElements.define("camp-gwpbanner", Gwpbanner);
